import React from 'react';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { badValuesArray } from '../../ServiceEvents/utils';
import { trakrefBlue, inputGrey, Grey4, red } from '../../../color_utils';
import { trakrefFontFamily, trakrefFontFamilySemiBold } from '../../../utils';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PropTypes from 'prop-types';

const getMarginTop = (variant) => {
    const marginTop = { standard: "0.8vh", filled: "1.8vh", outlined: "0vh" };
    return marginTop[variant];
};

const SelectOption = ({
    id,
    disabled,
    required,
    error,
    value,
    label,
    options,
    onChange,
    onClose,
    width,
    variant,
    menuProps,
    sx,
    showNullOption,
    disabledMenuItems,
    disableUnderline,
    customHelperText,
    customCreateNewItemText,
    transformLabel,
    labelColor,
    dropdownIconSize,
    renderValue,
    arrowColor,
    isFilterObject,
    displayEmpty,
    labelTopMargin,
    renderHelperText,
}) => {

    const valueSelector = (option) => {
        if (isFilterObject) {
            return option;
        }
        return option.value ? option.value : option.id;
    };

    const determineIcon = () => {
        if (error) {
            return [ErrorOutlineIcon, red];
        }
        if (disabled) {
            return [KeyboardArrowDownIcon, 'rgba(0, 0, 0, 0.26)'];
        }
        return [KeyboardArrowDownIcon, arrowColor];
    };

    const determineLabelStyle = () => {
        let styling = {
            "&.Mui-focused": { color: trakrefBlue },
            fontFamily: trakrefFontFamily
        };

        if (variant === "standard") {
            styling = {
                ...styling,
                "&.MuiFormLabel-root.MuiInputLabel-root": { left: "-13px" }
            };
        }
        return styling;
    };

    const determineHelperTextStyle = () => {
        let styling = {};

        if (variant === "standard") {
            styling = { "&.MuiFormHelperText-root": { marginLeft: 0 } };
        }
        return styling;
    };
    
    const determineLabelTopMargin = () => {
        if (labelTopMargin !== null) return labelTopMargin;
        if (variant === "standard") return "0.8vh";
        return "1.8vh";
    };

    const labelStyle = determineLabelStyle();
    const helperTextStyle = determineHelperTextStyle();
    const [SelectIcon, selectIconColor] = determineIcon();

    const defaultStyles = {
        '& .MuiSelect-select': {
            fontFamily: trakrefFontFamily,
            '&:focus': { backgroundColor: 'transparent' }
        },
        '& .MuiSelect-filled': { backgroundColor: inputGrey },
        '& .MuiSelect-filled.Mui-disabled': { backgroundColor: Grey4 },
        '& .MuiSvgIcon-root ': { fill: selectIconColor, fontSize: dropdownIconSize },
        '& .MuiFormLabel-root': { color: trakrefBlue, fontFamily: trakrefFontFamily },
        '& .MuiInputLabel-root': { color: trakrefBlue, fontFamily: trakrefFontFamily },
        '& .MuiInputBase-input': { textAlign: 'left' },
        '&:after': { borderColor: trakrefBlue },
    };

    return (
        <FormControl
            sx={{
                width: `${width}%`,
                '& .MuiFormLabel-root.MuiInputLabel-shrink': {
                    textTransform: transformLabel && "uppercase",
                    marginTop: getMarginTop(variant),
                    left: variant.toLowerCase() === "standard" ? "-14px" : "-2px",
                    color: disabled ? 'rgba(0, 0, 0, 0.38)' : labelColor,
                    fontFamily: trakrefFontFamilySemiBold,
                },
                "& .MuiFormLabel-root.MuiInputLabel-root.Mui-error": {
                    color: "#D51A1A !important",
                },
                "& .MuiInputBase-root.MuiInput-underline.Mui-error:after": {
                    borderBottom: "2px solid #D51A1A !important",
                },
            }}
            required={required}
            error={error}
            disabled={disabled}
        >
            <InputLabel shrink={!badValuesArray.includes(value)} sx={labelStyle}>
                {label}
            </InputLabel>
            <Select
                id={id}
                displayEmpty={displayEmpty}
                disableUnderline={disableUnderline}
                value={value}
                label={label}
                onChange={onChange}
                onClose={onClose}
                notched={!badValuesArray.includes(value)}
                variant={variant}
                MenuProps={menuProps}
                IconComponent={SelectIcon}
                sx={{ ...defaultStyles, ...sx }}
                renderValue={renderValue}
            >
                {showNullOption && (
                    <MenuItem
                        key=""
                        value=""
                        id=""
                    >
                        {customCreateNewItemText ? customCreateNewItemText : '-- Please Select --'}
                    </MenuItem>
                )}
                {options.map(option => (
                    <MenuItem
                        testing_id={`${id}_${(option.id ? option.id : option)}`}
                        key={valueSelector(option)}
                        value={valueSelector(option)}
                        id={valueSelector(option)}
                        disabled={disabledMenuItems.includes(valueSelector(option))}
                    >
                        {option.label ? option.label : (option.name ? option.name : option)}
                    </MenuItem>
                ))}
            </Select>
            {error && customHelperText === false && (
                <FormHelperText sx={helperTextStyle}>
                    This field is required
                </FormHelperText>
            )}
            {error && customHelperText !== false && (
                <FormHelperText sx={helperTextStyle}>
                    {customHelperText}
                </FormHelperText>
            )}
            {renderHelperText && customHelperText && (
                <FormHelperText sx={helperTextStyle}>
                    {customHelperText}
                </FormHelperText>
            )}
        </FormControl>
    );
};

SelectOption.propTypes = {
    value: PropTypes.string.isRequired,
    label: PropTypes.string,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    width: PropTypes.number,
    variant: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    menuProps: PropTypes.object,
    sx: PropTypes.object,
    showNullOption: PropTypes.bool,
    disabledMenuItems: PropTypes.array,
    disableUnderline: PropTypes.bool,
    customHelperText: PropTypes.string,
    displayEmpty: PropTypes.bool,
    id: PropTypes.string,
    customCreateNewItemText: PropTypes.string,
    transformLabel: PropTypes.bool,
    labelColor: PropTypes.string,
    dropdownIconSize: PropTypes.string,
    renderValue: PropTypes.node,
    arrowColor: PropTypes.string,
    onClose: PropTypes.func,
    labelTopMargin: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    renderHelperText: PropTypes.bool,
};

SelectOption.defaultProps = {
    label: "",
    width: 100,
    variant: "outlined",
    required: false,
    disabled: false,
    error: false,
    menuProps: {},
    sx: {},
    showNullOption: true,
    disabledMenuItems: [],
    disableUnderline: false,
    displayEmpty: false,
    customHelperText: false,
    id: "",
    transformLabel: true,
    labelColor: trakrefBlue,
    dropdownIconSize: "16px",
    renderValue: null,
    arrowColor: trakrefBlue,
    labelTopMargin: null,
    renderHelperText: false,
};

export default SelectOption;